/**
 * `defineAdminRoute` — the admin registry's authoring helper
 * ([ADR 062 — Access Grants](../../../../../docs/adrs/062_STAFF_ACCESS_GRANTS.md) § 3).
 *
 * Identical to core's `defineRoute` except that every verb MUST declare the
 * grant it needs and how its tenant is resolved. `apiHandler` reads both off
 * the entry and enforces them before the handler body runs, so a route can no
 * longer omit the check — omission is a compile error rather than a review
 * catch. That is the entire point of BL-1832.
 *
 * **Why this is admin-only rather than a change to core's `VerbSpec`.**
 * `VerbSpec` is shared: 247 admin entries and 48 bidder ones. Bidder-web is
 * customer-facing and has no staff grants at all, so a required field on the
 * shared type would break 48 declarations to describe something that does not
 * apply to them. `AdminVerbSpec` **extends** `VerbSpec` instead — the ADR 027
 * response/query carriers keep working unchanged, and core stays untouched.
 *
 * **Opting out is a declaration too.** A verb that needs no grant says why, as
 * `grant: { none: '<kind> — <why>' }`, where the kind is one of
 * {@link NONE_REASON_KINDS}; a verb with no tenant says `tenant: { from: 'none',
 * reason }`. Both are checked at module load. The generated access table
 * (`docs/feature/admin/access-table.generated.md`, BL-1833 AC 7) lists every
 * declaration, with the opt-outs grouped by kind.
 */
import type { TenantOwnedRow, TenantTableKey } from '@auctogon/db/tenant-sources';

import { defineRoute } from '@auctogon/core/api/registry';
import type { CallerKind, HttpVerb, RouteEntry, VerbSpec } from '@auctogon/core/api/registry';

import type { AnyOfGrant, Grant, GrantDeclaration } from '../../isomorphic/auth/grants';
import { isAnyOfGrant, isNoGrant, isSuperAdminGrant } from '../../isomorphic/auth/grants';

import { assertVerbDeclared } from './verb-declaration';

/**
 * Re-exported for registry authors. Which gated table a declaration keys off — a KEY into `TENANT_SOURCES`
 * (`@auctogon/db/tenant-sources`), never the Drizzle table itself.
 *
 * **This is the one shape the registry cannot get wrong.** The admin registry
 * is imported by `api-client/typed-client.ts`, which is client-side, so every
 * registry module reaches the browser bundle. `@auctogon/db/schema` is
 * `server-only`; naming the table by VALUE here — as ADR 062 § 3 originally
 * sketched — would pull a server-only module into a client graph and fail the
 * Next build outright. A key is a plain string, so the registry stays pure
 * data, and the import above is `import type`, which is erased entirely.
 *
 * Safety is not lost by going through a string: the union is derived from
 * `TENANT_SOURCES` itself (`keyof typeof`), so an unknown key is a compile
 * error and a table added to the map is immediately nameable here. The key
 * also carries the row type forward — see `AdminVerbSpec`.
 */
export type { TenantTableKey } from '@auctogon/db/tenant-sources';

/**
 * A nested route's parent check: the loaded row's `column` must equal the
 * path param `param` (BL-1833 PR 3b). `auctions/:id/lots/:lotId/*` gates on the
 * LOT and declares `within: { column: 'auctionId', param: 'id' }`, so a lot
 * addressed under the wrong auction — or a home lot with no auction — is the
 * gate's 404 before the handler runs, instead of a hand-written check each
 * route could forget.
 */
export interface WithinParent<K extends TenantTableKey> {
  /** A column of `K`'s row — typed, so a misspelling cannot silently match nothing. */
  column: Extract<keyof TenantOwnedRow<K>, string>;
  /** The PATH param naming the parent. Load-time checked against the route's `params`. */
  param: string;
}

/**
 * A record whose permission depends on a value it carries (BL-1833 PR 6b):
 * the loaded row's `column` picks the grant from `grants`. `internal-notes/:noteId`
 * gates on the NOTE and declares `grantBy: { column: 'entityType', grants: … }`,
 * because a note's permission is its entity's — a customer note needs a
 * customer grant, a lot note a lot one — and the entity type is only known once
 * the note is loaded.
 *
 * A value the map does not list is the gate's 404, the same answer as a missing
 * row: there is no grant to check, so nothing could make it safe, and a 403
 * would confirm the row exists. The verb's own `grant` must be a reasoned
 * `{ none }`, as on a `polymorphic` route — the row's grant replaces it.
 */
export interface GrantByColumn<K extends TenantTableKey> {
  /** A column of `K`'s row — typed, so a misspelling cannot silently match nothing. */
  column: Extract<keyof TenantOwnedRow<K>, string>;
  /** Each value the column may hold, with the grant it requires. */
  grants: Readonly<Record<string, PolymorphicEntityGrant>>;
}

/**
 * `from: 'record'` — tenant read off a row loaded by primary key.
 *
 * Distributed over the table keys so `within.column` is checked against the
 * row of the table the declaration actually names.
 */
export type RecordSource = { [K in TenantTableKey]: RecordSourceFor<K> }[TenantTableKey];

/** {@link RecordSource} for one table. */
export interface RecordSourceFor<K extends TenantTableKey> {
  from: 'record';
  table: K;
  /** The `params`/query/body key holding the row's id. */
  param: string;
  /**
   * Where that key lives. Defaults to `'path'`. `'body'` (BL-1833 PR 2b) is for
   * a write that names its target in the JSON body — `invite/cancel`'s
   * `inviteId` — and makes the gate read the body before resolving.
   */
  in?: 'path' | 'query' | 'body';
  /**
   * Serve a soft-deleted row (BL-1833). Only for a view whose job is to show an
   * archived record — `GET auctions/[id]` — or a write that acts on one: restore,
   * or a repeat note delete answered as already deleted (BUG-2060). Tenant and
   * grant are still checked.
   */
  withDeleted?: true;
  /**
   * RETIRED (BL-1833) — was declarable but never resolved, and no route needed
   * it. Typed `never` rather than simply removed: `defineAdminRoute` infers its
   * methods generically, which switches off excess-property checks, so a
   * leftover `parent` would otherwise compile and be silently ignored by the
   * gate. The first route whose child row has no tenant column adds a real
   * resolver.
   */
  parent?: never;
  /** The row must also belong to the path's parent — see {@link WithinParent}. */
  within?: WithinParent<K>;
  /** The row's own value picks the grant — see {@link GrantByColumn}. */
  grantBy?: GrantByColumn<K>;
}

/**
 * One entity a `polymorphic` route accepts.
 *
 * Usually a row the id loads (`table`). A STAGED upload (BL-1436's
 * `draft_media` / `draft_document`) has no row yet — its id is a
 * client-minted session id — so its tenant is the one the body states
 * (`tenantField`), validated against membership exactly like `input`.
 */
export type PolymorphicEntity =
  | {
      table: TenantTableKey;
      grant: PolymorphicEntityGrant;
      /**
       * BL-2838 — the same entity for a record that does not exist yet. A request that states `newRecord: true`
       * in the body is not loaded by id: its tenant is the body's `tenantField`, validated against membership
       * like `input`, and the grant is this one (the CREATE grant, not the edit grant). The id is the one the
       * Add form minted; it names nothing until a create claims it.
       */
      newRecord?: { tenantField: string; grant: PolymorphicEntityGrant };
    }
  | { tenantField: string; grant: PolymorphicEntityGrant };

/**
 * An entity's grant: one grant, or any one of several (BL-1871 — the
 * place-in-auction route keeps its `auction.update` / `lot.hammer` pair on each
 * target kind). Never the `{ none }` opt-out — an entity is the route's only
 * real grant, so opting it out would leave the verb ungated.
 */
export type PolymorphicEntityGrant = Grant | AnyOfGrant;

/**
 * How a route's tenant is resolved. A **closed** set — ADR 062 § 3 classified
 * all 246 admin routes and 240 fit nine kinds, with the two misfit families
 * (`polymorphic`, `shared_membership`) given named kinds rather than an
 * escape. The sweep's `deferred` placeholder was retired in BL-1833 PR 7, when
 * no route carried it, and BL-2413 deleted `storage_object` once its one route
 * (`files/download`) was retired for per-record downloads.
 *
 * The kind decides the ORDER `apiHandler` runs its gates in (AC 5): a kind
 * whose tenant or target lives in the body has the body read first; everything
 * else resolves first. The route never chooses.
 */
export type TenantSource =
  /** Tenant read off a row loaded by primary key. 116 routes — the common case. */
  | RecordSource
  /** Bulk: an array of ids that must all belong to one tenant. Mixed → 400. */
  | {
      from: 'records';
      table: TenantTableKey;
      field: string;
      /**
       * The most ids one request may name — REQUIRED (BL-1832). The gate
       * resolves ids against the database before the handler runs, so a bound
       * the handler enforces arrives too late: an unbounded list would reach the
       * database as an unbounded `IN (...)` first. The gate refuses anything
       * larger before it queries.
       */
      max: number;
    }
  /** The caller states the tenant; membership still validates the claim. */
  | {
      from: 'input';
      field: string;
      in: 'query' | 'body';
      /**
       * The tenant may be ABSENT (BL-1832, `user/list`). Then the request has
       * no tenant, and only a super admin passes the grant check — a route
       * whose absent-tenant branch serves anyone else must not set this. Left
       * unset, an absent tenant is a 400.
       */
      optional?: true;
    }
  /** The caller's own tenant, still intersected with membership. */
  | { from: 'session' }
  /**
   * A cross-tenant list or bulk read, narrowed to the caller's permitted set
   * (BL-1833 PR 3a). The gate resolves every tenant where the caller holds the
   * verb's grant — any one of an `anyOf` — and hands the handler that SET; it
   * 403s only when the set is empty. For routes that take ids from several of
   * the caller's tenants at once (`lots/batch-check` from `/inventory`) and
   * filter rather than refuse. Needs a real grant: `{ none }` is refused at load.
   */
  | { from: 'scoped' }
  /** `files/*` — the entity type arrives in the request and picks the table. */
  | {
      from: 'polymorphic';
      /**
       * Where `typeField` and `idField` live. Defaults to `'body'`. `'query'`
       * (BL-1833 PR 6b) is for a READ that names its entity in the query
       * string — `GET internal-notes?entityType=&entityId=` — and makes the
       * gate skip the body read. A row-less (`tenantField`) entity cannot
       * pair with it: its tenant is a body field, and a GET has no body.
       */
      in?: 'body' | 'query';
      /** The field naming the entity type, e.g. `'entityType'`. */
      typeField: string;
      /** The field holding the entity's id, e.g. `'entityId'`. */
      idField: string;
      /**
       * Every entity type the route accepts, with the table its id resolves in
       * and the grant it requires. An unlisted type is a 400.
       *
       * **The grant is per entity, not per verb** (BL-1832, ADR 062 § 3: files
       * inherit their parent entity's gate). One coarse grant cannot be right
       * here — `file.delete` maps to inventory.manage, which would refuse an
       * Accounting user the invoice attachment they can delete today. The
       * verb's own `grant` is therefore a reasoned opt-out on a polymorphic
       * route, and the gate enforces the entity's instead.
       */
      entities: Readonly<Record<string, PolymorphicEntity>>;
    }
  /**
   * `user/update`, `user/delete`, `user/tenant-roles` — a write about ANOTHER
   * staff member, at a tenant the caller states (BL-1833 PR 2b). The caller's
   * membership is checked like `input`; then, AFTER the grant, the gate checks
   * the target named by `memberField` is a live member of that same tenant and
   * answers 404 otherwise. Grant first so a caller without it cannot use the
   * route to probe who belongs to their tenant.
   */
  | { from: 'shared_membership'; tenantField: string; memberField: string; in: 'body' }
  /** Genuinely tenant-less: cron, webhook, self-service, public. */
  | { from: 'none'; reason: string };

/**
 * A verb's contract: core's {@link VerbSpec} plus the two declarations this ADR
 * adds. Both are REQUIRED — that requirement is the mechanism.
 */
export interface AdminVerbSpec<R = unknown> extends VerbSpec<R> {
  /** The grant a caller must hold, or a reasoned opt-out. */
  grant: GrantDeclaration;
  /** How to find the tenant this request acts on. */
  tenant: TenantSource;
  /**
   * Optional cap, in bytes, on a JSON body the gate itself reads (a body-sourced tenant). The gate refuses a
   * larger body with 413 before it parses it, from `Content-Length` and again on the bytes actually read.
   * Unset keeps today's behavior (no cap) for every other route.
   */
  maxBodyBytes?: number;
}

/** The `methods` map an admin route declares. */
export type AdminMethods = Partial<Record<HttpVerb, AdminVerbSpec>>;

/**
 * Each verb spec, intersected with core's {@link VerbSpec}.
 *
 * Needed because core's `VerbSpec` is a **weak type** — every property on it is
 * optional — and TypeScript refuses to assign an object that shares *no*
 * property with a weak type. A declaration of `{ grant, tenant }` and nothing
 * else shares none, so without this the whole registry fails to satisfy
 * `RegistryTree` at `createRegistry(...)`, with an error that points at the
 * tree rather than at the cause.
 *
 * Intersecting rather than widening is what keeps the chain alive: the literal
 * `table: 'invoice'` survives, so `apiHandler` can still infer the handler's
 * record type from the entry.
 */
type WithCoreVerbSpec<M extends AdminMethods> = { [K in keyof M]: M[K] & VerbSpec };

/**
 * Declare an admin API route.
 *
 * @throws at module load when an opt-out carries no reason, or a `{ none }`
 *   reason names no kind from {@link NONE_REASON_KINDS} — the
 *   same failure mode as `createRegistry`'s duplicate-path guard, so a bad
 *   declaration fails the first import rather than a code review. TypeScript
 *   cannot express "non-empty string" here (see {@link GrantDeclaration}).
 */
export function defineAdminRoute<
  P extends Record<string, string>,
  M extends AdminMethods,
>(entry: {
  path: string;
  params: P;
  methods: M;
  caller?: CallerKind;
}): RouteEntry<P, WithCoreVerbSpec<M>> {
  for (const [verb, spec] of Object.entries(entry.methods)) {
    if (!spec) continue;
    assertVerbDeclared({ path: entry.path, verb, spec });
    assertReason(entry.path, verb, 'grant', isNoGrant(spec.grant) ? spec.grant.none : null);
    if (isNoGrant(spec.grant)) assertNoneKind(entry.path, verb, spec.grant.none);
    assertReason(entry.path, verb, 'grant', isSuperAdminGrant(spec.grant) ? spec.grant.superAdmin : null);
    if (
      isSuperAdminGrant(spec.grant) &&
      (spec.tenant.from === 'scoped' || spec.tenant.from === 'polymorphic')
    ) {
      // `scoped` builds its set from role grants, which a super-admin-only
      // declaration has none of; `polymorphic` enforces the per-entity grant
      // INSTEAD of the verb's, so this one would be ignored.
      throw new Error(
        `[api-registry] ${verb} ${entry.path}: a { superAdmin } grant cannot pair with a ` +
          `'${spec.tenant.from}' tenant. Use 'none' or 'record'.`,
      );
    }
    if (isAnyOfGrant(spec.grant)) assertDistinct(entry.path, verb, spec.grant.anyOf);
    if (spec.tenant.from === 'polymorphic' && !isNoGrant(spec.grant)) {
      // The gate enforces the entity's grant INSTEAD of the verb's, so a verb
      // grant here would read as enforced while being ignored. `{ superAdmin }`
      // is refused above with its own message.
      throw new Error(
        `[api-registry] ${verb} ${entry.path}: a 'polymorphic' tenant takes its grant from ` +
          `each entity — declare the verb's grant as { none: '<reason>' }.`,
      );
    }
    if (spec.tenant.from === 'polymorphic') assertPolymorphic(entry.path, verb, spec.tenant);
    if (spec.tenant.from === 'scoped' && isNoGrant(spec.grant)) {
      // The scope IS "the tenants where you hold this grant" — with no grant
      // there is nothing to narrow by, and every member tenant would pass.
      throw new Error(
        `[api-registry] ${verb} ${entry.path}: a 'scoped' tenant needs a real grant to scope by, ` +
          `not { none }.`,
      );
    }
    if (spec.tenant.from === 'record') {
      assertRecord({ path: entry.path, verb, params: entry.params, grant: spec.grant, source: spec.tenant });
    }
    assertReason(
      entry.path,
      verb,
      'tenant',
      spec.tenant.from === 'none' ? spec.tenant.reason : null,
    );
  }
  // Sound by construction: every `AdminVerbSpec` already extends `VerbSpec`, so
  // `M[K] & VerbSpec` is `M[K]`. The cast states that to tsc, which cannot see
  // it through the mapped type.
  return defineRoute(entry) as RouteEntry<P, WithCoreVerbSpec<M>>;
}

/**
 * The load-time rules a `polymorphic` declaration must meet: each entity's
 * any-of is distinct, and a query-sourced one lists no row-less entity.
 *
 * @param path - The route's path, for the error message.
 * @param verb - The verb being declared, for the error message.
 * @param source - The verb's polymorphic tenant declaration.
 * @throws when either rule is broken.
 */
function assertPolymorphic(
  path: string,
  verb: string,
  source: Extract<TenantSource, { from: 'polymorphic' }>,
): void {
  for (const [type, entity] of Object.entries(source.entities)) {
    if (isAnyOfGrant(entity.grant)) assertDistinct(path, verb, entity.grant.anyOf);
    if ('newRecord' in entity && entity.newRecord && isAnyOfGrant(entity.newRecord.grant)) {
      assertDistinct(path, verb, entity.newRecord.grant.anyOf);
    }
    if (source.in === 'query' && 'newRecord' in entity && entity.newRecord) {
      throw new Error(`[api-registry] ${verb} ${path}: entity '${type}' declares newRecord, which reads the body. An in: 'query' declaration never does.`);
    }
    if (source.in === 'query' && 'tenantField' in entity) {
      // A staged entity's tenant is a BODY field, and the gate does not read
      // the body for a query-sourced declaration — so the tenant would read as
      // absent and every request 400.
      throw new Error(
        `[api-registry] ${verb} ${path}: entity '${type}' takes its tenant from the body ` +
          `('tenantField'), which an in: 'query' declaration never reads. Give it a table.`,
      );
    }
  }
}

/**
 * The load-time rules a `record` declaration must meet: `within` names a path
 * param, and a `grantBy` verb opts out of its own grant (the row's replaces
 * it) with every any-of distinct.
 *
 * @param opts - The route's path and params, the verb, its grant, and its record source.
 * @throws when a rule is broken.
 */
function assertRecord(opts: {
  path: string;
  verb: string;
  params: Record<string, string>;
  grant: GrantDeclaration;
  source: RecordSource;
}): void {
  const { path, verb, source } = opts;
  if (source.within && !Object.prototype.hasOwnProperty.call(opts.params, source.within.param)) {
    // The gate reads `within.param` off the PATH; a key the path does not
    // declare would read undefined and 404 every request.
    throw new Error(
      `[api-registry] ${verb} ${path}: within names '${source.within.param}', which is not a path param.`,
    );
  }
  if (!source.grantBy) return;
  if (!isNoGrant(opts.grant)) {
    // The gate enforces the ROW's grant instead of the verb's, so a verb grant
    // here would read as enforced while being ignored — and a { superAdmin }
    // one would silently admit every role the row's grant admits.
    throw new Error(
      `[api-registry] ${verb} ${path}: a record with grantBy takes its grant from the row — ` +
        `declare the verb's grant as { none: '<reason>' }.`,
    );
  }
  for (const grant of Object.values(source.grantBy.grants)) {
    if (isAnyOfGrant(grant)) assertDistinct(path, verb, grant.anyOf);
  }
}

/**
 * Fail loudly when an any-of names the same grant twice.
 *
 * A duplicate cannot widen access, but it makes the declaration read as two
 * roles' permissions when it is one — and the generated "who may call what"
 * table (BL-1833 AC 7) would then describe the route wrongly.
 */
function assertDistinct(path: string, verb: string, grants: readonly Grant[]): void {
  const seen = new Set<Grant>();
  for (const grant of grants) {
    if (seen.has(grant)) {
      throw new Error(
        `[api-registry] ${verb} ${path}: 'grant.anyOf' lists ${grant} twice. ` +
          `Name each grant once — the list is read as the set of permissions that admit a caller.`,
      );
    }
    seen.add(grant);
  }
}

/**
 * Fail loudly when an escape hatch carries no reason.
 *
 * `null` means "this field is not an escape hatch" and is always fine. An empty
 * or whitespace-only string means someone reached for the escape without saying
 * why, which is indistinguishable from an oversight and defeats the BL-1833
 * grep.
 */
function assertReason(
  path: string,
  verb: string,
  field: 'grant' | 'tenant',
  reason: string | null,
): void {
  if (reason === null) return;
  if (reason.trim().length > 0) return;
  throw new Error(
    `[api-registry] ${verb} ${path}: '${field}' opts out but states no reason. ` +
      `Give it one — an unexplained opt-out cannot be told apart from a route that ` +
      `simply forgot to declare, and the BL-1833 sweep finds these by their reason text.`,
  );
}

/**
 * The kinds a `{ none }` grant opt-out may be. Every opt-out reason reads
 * `'<kind> — <why>'`, so the population can be reviewed as a set (BL-1833
 * AC 6): the generated access table groups the opt-outs by these kinds.
 *
 * - `pre-auth` — runs before a staff session exists (Better Auth, invites).
 * - `token` — a one-time token, nonce or unguessable id is the credential.
 * - `public` — serves no user, tenant or database data.
 * - `self` — the caller's own session, profile or preferences.
 * - `cron` — a scheduler secret or OIDC token authorizes it.
 * - `webhook` — a vendor signature authorizes it.
 * - `per entity` / `per entity type` / `per target kind` — the gate enforces a
 *   grant per resolved record (`polymorphic`, `grantBy`); the verb has none of
 *   its own.
 *
 * A route that needs no grant for any other reason does not exist yet. The
 * first one adds its kind here, in review, rather than hiding in free text.
 * Longer kinds sort after the kinds they extend; {@link noneReasonKind} matches
 * the longest.
 */
export const NONE_REASON_KINDS = [
  'pre-auth',
  'token',
  'public',
  'self',
  'cron',
  'webhook',
  'per entity',
  'per entity type',
  'per target kind',
] as const;

/** One of {@link NONE_REASON_KINDS}. */
export type NoneReasonKind = (typeof NONE_REASON_KINDS)[number];

/**
 * The kind a `{ none }` reason declares, or `null` when it declares none.
 *
 * @param reason - The opt-out's reason text.
 * @returns The longest kind the reason starts with, followed by ` — ` and some
 *   explanation; `null` otherwise.
 */
export function noneReasonKind(reason: string): NoneReasonKind | null {
  let match: NoneReasonKind | null = null;
  for (const kind of NONE_REASON_KINDS) {
    const rest = reason.startsWith(`${kind} — `) ? reason.slice(kind.length + 3) : '';
    if (rest.trim().length > 0 && (!match || kind.length > match.length)) match = kind;
  }
  return match;
}

/**
 * Fail loudly when a `{ none }` reason declares no kind. An empty reason is
 * {@link assertReason}'s to report, so this one stays quiet on it.
 */
function assertNoneKind(path: string, verb: string, reason: string): void {
  if (reason.trim().length === 0 || noneReasonKind(reason)) return;
  throw new Error(
    `[api-registry] ${verb} ${path}: '{ none }' reason "${reason}" names no kind. ` +
      `Write it as '<kind> — <why>', where kind is one of: ${NONE_REASON_KINDS.join(', ')}.`,
  );
}
